import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Search, Filter, X, Loader2, Building, ChevronDown, PanelLeft, Car, User, Phone, MapPin, ShieldAlert, Clock } from 'lucide-react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { getApiBaseUrl } from '../services/api';
import { authService } from '../services/authService';
import AppHeader from './AppHeader';

const SIDEBAR_MIN_WIDTH = 240;
const SIDEBAR_MAX_WIDTH = 520;
const SIDEBAR_DEFAULT_WIDTH = 320;

// Ztime returns every appointment for an office, cancelled ones included, so the list is dominated
// by cancellations. Default to the appointments that still matter and let the user opt in to the rest.
const ACTIVE_STATUSES = ['BOOKED', 'CONFIRMED', 'RESCHEDULED', 'INITIATED'];
const CANCELLED_STATUSES = ['CANCELED', 'CANCELLED'];
const DEFAULT_STATUS_FILTER = 'ACTIVE';

const AppointmentsManagement = ({ user, onLogout, defaultEnterprise, defaultBOC, defaultStore }) => {
  const location = useLocation();

  // Role check - only ADMIN and SUPER_ADMIN can access
  if (user?.role !== 'ADMIN' && user?.role !== 'SUPER_ADMIN') {
    return <Navigate to="/" replace />;
  }

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  
  const [enterprises, setEnterprises] = useState([]);
  const [bocs, setBocs] = useState([]);
  const [stores, setStores] = useState([]);
  const [officeMappings, setOfficeMappings] = useState([]);
  const [selectedEnterprise, setSelectedEnterprise] = useState(null);
  const [selectedBOC, setSelectedBOC] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [keywordFilter, setKeywordFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS_FILTER);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingEnterprises, setLoadingEnterprises] = useState(false);
  const [loadingBOCs, setLoadingBOCs] = useState(false);
  const [loadingStores, setLoadingStores] = useState(false);
  const [error, setError] = useState(null);

  // Holds the user's saved preferences so the loaders can apply them as
  // defaults even when the props are null (e.g. page reloaded directly on
  // the appointments screen).
  const preferencesRef = useRef(null);

  useEffect(() => {
    const init = async () => {
      // Load preferences first so they are available when the pickers apply defaults
      await loadPreferences();
      loadEnterprises();
      loadOfficeMappings();
    };
    init();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await authService.apiCall('/auth/preferences');
      if (response.ok) {
        preferencesRef.current = await response.json();
      }
    } catch (err) {
      console.error('Error loading user preferences:', err);
    }
  };

  const getDefaultEnterprise = () => defaultEnterprise ?? preferencesRef.current?.enterpriseIdFk ?? null;
  const getDefaultBOC = () => defaultBOC ?? preferencesRef.current?.bocIdFk ?? null;
  const getDefaultStore = () => defaultStore ?? preferencesRef.current?.storeIdFk ?? null;

  useEffect(() => {
    filterAppointments();
  }, [appointments, keywordFilter, statusFilter, startDate, endDate]);

  const loadEnterprises = async () => {
    setLoadingEnterprises(true);
    setError(null);
    try {
      const response = await authService.apiCall('/admin/enterprises');
      if (response.ok) {
        const data = await response.json();
        setEnterprises(data);
        // Set default enterprise if provided and valid
        const preferredEnterprise = getDefaultEnterprise();
        if (preferredEnterprise && data.find(e => e.id === preferredEnterprise)) {
          setSelectedEnterprise(preferredEnterprise);
        }
      } else {
        setError('Failed to load enterprises');
      }
    } catch (err) {
      console.error('Error loading enterprises:', err);
      setError('Failed to load enterprises');
    } finally {
      setLoadingEnterprises(false);
    }
  };

  useEffect(() => {
    if (selectedEnterprise) {
      loadBOCs(selectedEnterprise);
    }
  }, [selectedEnterprise]);

  useEffect(() => {
    if (selectedBOC) {
      loadStores(selectedBOC);
    }
  }, [selectedBOC]);

  const loadBOCs = async (enterpriseId) => {
    setLoadingBOCs(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/bocs/enterprise/${enterpriseId}`, {
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setBocs(data);
        // Set default BOC if provided and valid
        const preferredBOC = getDefaultBOC();
        if (preferredBOC && data.find(b => b.id === preferredBOC)) {
          setSelectedBOC(preferredBOC);
        }
      }
    } catch (err) {
      console.error('Error loading BOCs:', err);
    } finally {
      setLoadingBOCs(false);
    }
  };

  const loadStores = async (bocId) => {
    setLoadingStores(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/stores/boc/${bocId}`, {
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStores(data);
        // Set default store if provided and valid
        const preferredStore = getDefaultStore();
        if (preferredStore && data.find(s => s.id === preferredStore)) {
          setSelectedStore(preferredStore);
        }
        return data;
      }
    } catch (err) {
      console.error('Error loading stores:', err);
    } finally {
      setLoadingStores(false);
    }
  };

  const loadOfficeMappings = async () => {
    try {
      const response = await authService.apiCall('/admin/office-mapping');
      if (response.ok) {
        const data = await response.json();
        setOfficeMappings(data);
      }
    } catch (err) {
      console.error('Error loading office mappings:', err);
    }
  };

  const handleEnterpriseChange = async (enterpriseId) => {
    setSelectedEnterprise(enterpriseId);
    setSelectedBOC(null);
    setSelectedStore(null);
    setSelectedOffice(null);
    setBocs([]);
    setStores([]);
    setAppointments([]);
    setFilteredAppointments([]);
    if (enterpriseId) {
      await loadBOCs(enterpriseId);
    }
  };

  const handleBOCChange = async (bocId) => {
    setSelectedBOC(bocId);
    setSelectedStore(null);
    setSelectedOffice(null);
    setStores([]);
    setAppointments([]);
    setFilteredAppointments([]);
    if (bocId) {
      await loadStores(bocId);
    }
  };

  const handleStoreChange = (storeId) => {
    setSelectedStore(storeId);
    setSelectedOffice(null);
    setAppointments([]);
    setFilteredAppointments([]);
  };

  // Resolve the office mapping whenever a store is selected - covers both
  // manual selection and stores auto-selected from user preferences. Waiting
  // on officeMappings here also avoids the race where the store default is
  // applied before the mappings finish loading.
  useEffect(() => {
    if (!selectedStore || officeMappings.length === 0) return;
    const officeMapping = officeMappings.find(om => om.storeIdFk === selectedStore);
    if (!officeMapping) {
      setSelectedOffice(null);
      setError('No office mapping found for this store');
      return;
    }
    if (!selectedOffice || selectedOffice.id !== officeMapping.id) {
      setSelectedOffice(officeMapping);
      loadAppointments(officeMapping);
    }
  }, [selectedStore, officeMappings, selectedOffice]);

  const loadAppointments = async (office) => {
    if (!office) {
      setError('Please select an office');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        officeMasterId: office.officeMasterId
      });

      const response = await fetch(
        `${getApiBaseUrl()}/appointments/office?${params.toString()}`,
        {
          headers: authService.getAuthHeaders()
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAppointments(data);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to load appointments');
      }
    } catch (err) {
      console.error('Error loading appointments:', err);
      setError('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const filterAppointments = () => {
    let filtered = [...appointments];

    if (statusFilter === 'ACTIVE') {
      filtered = filtered.filter(apt => ACTIVE_STATUSES.includes(apt.appointmentStatus));
    } else if (statusFilter === 'CANCELLED') {
      filtered = filtered.filter(apt => CANCELLED_STATUSES.includes(apt.appointmentStatus));
    }

    if (keywordFilter.trim()) {
      const keyword = keywordFilter.toLowerCase();
      filtered = filtered.filter(apt => 
        (apt.customerName && apt.customerName.toLowerCase().includes(keyword)) ||
        (apt.custNo && apt.custNo.toLowerCase().includes(keyword)) ||
        (apt.cellular && apt.cellular.includes(keyword)) ||
        (apt.make && apt.make.toLowerCase().includes(keyword)) ||
        (apt.model && apt.model.toLowerCase().includes(keyword)) ||
        (apt.year && apt.year.toString().includes(keyword))
      );
    }

    if (startDate) {
      filtered = filtered.filter(apt => {
        if (!apt.appointmentDate) return false;
        const aptDate = new Date(apt.appointmentDate);
        return aptDate >= new Date(startDate);
      });
    }

    if (endDate) {
      filtered = filtered.filter(apt => {
        if (!apt.appointmentDate) return false;
        const aptDate = new Date(apt.appointmentDate);
        return aptDate <= new Date(endDate + 'T23:59:59');
      });
    }

    // Soonest first; appointments without a date go last. ISO timestamps sort correctly as strings.
    filtered.sort((a, b) => {
      if (!a.appointmentDate && !b.appointmentDate) return 0;
      if (!a.appointmentDate) return 1;
      if (!b.appointmentDate) return -1;
      return String(a.appointmentDate).localeCompare(String(b.appointmentDate));
    });

    setFilteredAppointments(filtered);
  };

  const handleSearch = () => {
    if (selectedOffice) {
      loadAppointments(selectedOffice);
    }
  };

  const handleClearFilters = () => {
    // The filter effect recomputes the list from these defaults.
    setKeywordFilter('');
    setStatusFilter(DEFAULT_STATUS_FILTER);
    setStartDate('');
    setEndDate('');
  };

  const handleMouseDown = (e) => {
    setIsResizing(true);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isResizing) return;
    const newWidth = e.clientX;
    if (newWidth >= SIDEBAR_MIN_WIDTH && newWidth <= SIDEBAR_MAX_WIDTH) {
      setSidebarWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'BOOKED':
      case 'CONFIRMED':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'CANCELED':
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'RESCHEDULED':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'INITIATED':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div
      className={`flex flex-col h-screen bg-gradient-to-br from-slate-50 to-slate-100 overflow-hidden ${
        isResizing ? 'cursor-col-resize select-none' : ''
      }`}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* TOP BAR */}
      <div className="flex items-center gap-3">
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            title="Open sidebar"
            aria-label="Open sidebar"
            aria-expanded={false}
            className="-ml-1 flex h-8 w-8 items-center justify-center rounded-md text-gray-700 transition-colors duration-150 hover:bg-gray-100/70 hover:text-gray-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-300"
          >
            <PanelLeft size={17} strokeWidth={1.5} />
          </button>
        )}
        <AppHeader 
          user={user} 
          onLogout={onLogout} 
          badgeCount={filteredAppointments.length}
          badgeLabel="Appointments"
        />
      </div>

      {/* BODY */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* LEFT SIDEBAR - Store Selection */}
        <div
          className={`bg-white flex-shrink-0 overflow-y-auto overflow-x-hidden shadow-lg ${
            isResizing ? '' : 'transition-[width] duration-300 ease-in-out'
          }`}
          style={{ width: sidebarOpen ? sidebarWidth : 0 }}
        >
          <div className="p-4" style={{ width: sidebarWidth }}>
            {/* Store Selection */}
            <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              <div className="px-4 py-3 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-gray-200 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-800 flex items-center">
                  <Building className="h-4 w-4 mr-2 text-emerald-600" />
                  Store Selection
                </span>
                <button
                  onClick={() => setSidebarOpen(false)}
                  title="Close sidebar"
                  aria-label="Close sidebar"
                  className="-mr-1 flex h-6 w-6 items-center justify-center rounded text-gray-400 transition-colors duration-150 hover:bg-white/70 hover:text-gray-600"
                >
                  <PanelLeft size={14} strokeWidth={1.5} />
                </button>
              </div>

              <div className="p-4 space-y-3">
                <div className="relative">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Enterprise</label>
                  <select
                    value={selectedEnterprise || ''}
                    onChange={(e) => handleEnterpriseChange(e.target.value ? Number(e.target.value) : null)}
                    disabled={loadingEnterprises}
                    className="block w-full pl-3 pr-10 py-2.5 text-sm border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 rounded-lg shadow-sm appearance-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                  >
                    <option value="">Select Enterprise</option>
                    {enterprises.map((ent) => (
                      <option key={ent.id} value={ent.id}>
                        {ent.name}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 mt-5">
                    <ChevronDown className="h-4 w-4" />
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">BOC</label>
                  <select
                    value={selectedBOC || ''}
                    onChange={(e) => handleBOCChange(e.target.value ? Number(e.target.value) : null)}
                    disabled={!selectedEnterprise}
                    className="block w-full pl-3 pr-10 py-2.5 text-sm border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 rounded-lg shadow-sm appearance-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                  >
                    <option value="">Select BOC</option>
                    {loadingBOCs ? (
                      <option disabled>Loading...</option>
                    ) : (
                      bocs.map((boc) => (
                        <option key={boc.id} value={boc.id}>
                          {boc.bocId}
                        </option>
                      ))
                    )}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 mt-5">
                    {loadingBOCs ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-500"></div>
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Store</label>
                  <select
                    value={selectedStore || ''}
                    onChange={(e) => handleStoreChange(e.target.value ? Number(e.target.value) : null)}
                    disabled={!selectedBOC}
                    className="block w-full pl-3 pr-10 py-2.5 text-sm border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 rounded-lg shadow-sm appearance-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                  >
                    <option value="">Select Store</option>
                    {loadingStores ? (
                      <option disabled>Loading...</option>
                    ) : (
                      stores.map((store) => (
                        <option key={store.id} value={store.id}>
                          {store.name}
                        </option>
                      ))
                    )}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 mt-5">
                    {loadingStores ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-500"></div>
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </div>

                {selectedOffice && (
                  <div className="mt-4 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-100">
                    <p className="text-xs font-medium text-gray-700 mb-1">Mapped Office</p>
                    <p className="text-sm font-semibold text-gray-900">{selectedOffice.officeCode}</p>
                    <p className="text-xs text-gray-600">{selectedOffice.dealerCode}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* RESIZE HANDLE */}
        {sidebarOpen && (
          <div
            className="w-1 bg-gray-200 hover:bg-emerald-400 cursor-col-resize flex-shrink-0 transition-colors"
            onMouseDown={handleMouseDown}
          />
        )}

        {/* MAIN CONTENT */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl shadow-sm">
              {error}
            </div>
          )}

          {/* Filters Bar */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Filter size={18} className="text-emerald-600" />
              <span className="text-sm font-semibold text-gray-800">Filters</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Keyword Filter */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center">
                  <Search size={14} className="mr-1.5 text-gray-500" />
                  Keyword Search
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={keywordFilter}
                    onChange={(e) => setKeywordFilter(e.target.value)}
                    placeholder="Search by customer, phone, vehicle..."
                    className="block w-full pl-3 pr-10 py-2.5 text-sm border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 rounded-lg shadow-sm transition-colors"
                  />
                  {keywordFilter && (
                    <button
                      onClick={() => setKeywordFilter('')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center">
                  <Calendar size={14} className="mr-1.5 text-gray-500" />
                  From Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="block w-full pl-3 pr-3 py-2.5 text-sm border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 rounded-lg shadow-sm transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center">
                  <Calendar size={14} className="mr-1.5 text-gray-500" />
                  To Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="block w-full pl-3 pr-3 py-2.5 text-sm border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 rounded-lg shadow-sm transition-colors"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-100">
              <button
                onClick={handleSearch}
                disabled={!selectedOffice || loading}
                className="flex items-center px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:bg-emerald-300 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <Loader2 size={16} className="mr-2 animate-spin" />
                ) : (
                  <Search size={16} className="mr-2" />
                )}
                Search Appointments
              </button>
              <button
                onClick={handleClearFilters}
                className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                <X size={16} className="mr-2" />
                Clear Filters
              </button>
              <div className="flex items-center gap-2">
                <label htmlFor="appointment-status-filter" className="text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  id="appointment-status-filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="block py-2 pl-3 pr-8 text-sm border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 rounded-lg shadow-sm bg-white transition-colors"
                >
                  <option value="ACTIVE">Active (booked / rescheduled)</option>
                  <option value="CANCELLED">Cancelled</option>
                  <option value="ALL">All</option>
                </select>
              </div>
              {filteredAppointments.length !== appointments.length && (
                <span className="text-xs text-gray-500">
                  Showing {filteredAppointments.length} of {appointments.length} appointments
                </span>
              )}
            </div>
          </div>

          {/* Appointments Grid */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            {!selectedStore ? (
              <div className="flex items-center justify-center py-20 text-center">
                <div className="max-w-sm">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Building size={32} className="text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a Store</h3>
                  <p className="text-gray-600 text-sm">Choose Enterprise → BOC → Store from the sidebar to view its appointments.</p>
                </div>
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <Loader2 size={40} className="mx-auto text-emerald-600 animate-spin mb-4" />
                  <p className="text-gray-600">Loading appointments...</p>
                </div>
              </div>
            ) : filteredAppointments.length === 0 ? (
              <div className="flex items-center justify-center py-20 text-center">
                <div className="max-w-sm">
                  <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Calendar size={32} className="text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Appointments Found</h3>
                  <p className="text-gray-600 text-sm">
                    {statusFilter === 'ACTIVE' && appointments.length > 0
                      ? 'No active appointments for this office. Change the Status filter to see cancelled ones.'
                      : 'Try adjusting your filters or select a different office.'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Appointment
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Phone
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Vehicle
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Date & Time
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Waiter
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {filteredAppointments.map((appointment, index) => (
                      <tr 
                        key={appointment.appointmentId} 
                        className={`hover:bg-gradient-to-r hover:from-emerald-50 hover:to-teal-50 transition-colors ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center mr-3">
                              <Calendar size={16} className="text-white" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">#{appointment.appointmentId}</div>
                              <div className="text-xs text-gray-500">{appointment.appointmentCode || '-'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center mr-3">
                              <User size={16} className="text-white" />
                            </div>
                            <div>
                              {appointment.customerName ? (
                                <div className="text-sm font-medium text-gray-900">{appointment.customerName}</div>
                              ) : (
                                <div className="text-sm italic text-gray-400">Unknown customer</div>
                              )}
                              <div className="text-xs text-gray-500">
                                {appointment.custNo ? `Cust #${appointment.custNo}` : '-'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Phone size={16} className="text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">{appointment.cellular || '-'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center mr-3">
                              <Car size={16} className="text-white" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {appointment.year && appointment.make && appointment.model
                                  ? `${appointment.year} ${appointment.make} ${appointment.model}`
                                  : '-'}
                              </div>
                              <div className="text-xs text-gray-500">{appointment.shopCode || '-'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Clock size={16} className="text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">{formatDate(appointment.appointmentDate)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStatusColor(appointment.appointmentStatus)}`}>
                            {appointment.appointmentStatus || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            appointment.waiter === 'Y' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {appointment.waiter === 'Y' ? 'Yes' : 'No'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentsManagement;
